import { Text, Anchor } from '@mantine/core';
import { useMemo } from 'react';
import { useExpandableText } from '@/hooks/useExpandableText';
import { generateHTML } from '@tiptap/core';
import { createRichTextExtensions } from '../RichTextInput/richTextExtensions';
import { MentionItem } from '../RichTextInput/mention';
import { JSONContent } from '@tiptap/react';
import { HighlightableTextProps, DEFAULT_HIGHLIGHT_STYLE, findMatches } from './shared';

const highlightHtmlContent = (html: string, term: string): string => {
  if (!term.trim()) return html;

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const fullText = doc.body.textContent || '';
  const matches = findMatches(fullText, term);

  if (matches.length === 0) return html;

  let globalOffset = 0;

  const processTextNode = (node: Text): void => {
    const text = node.textContent || '';
    const nodeStart = globalOffset;
    const nodeEnd = nodeStart + text.length;

    const relevantMatches = matches.filter((m) => m.start < nodeEnd && m.end > nodeStart);
    if (relevantMatches.length === 0) {
      globalOffset = nodeEnd;
      return;
    }

    const parent = node.parentNode;
    if (!parent) return;

    const fragments: Node[] = [];
    let cursor = 0;

    relevantMatches.forEach((match) => {
      const relativeStart = Math.max(0, match.start - nodeStart);
      const relativeEnd = Math.min(text.length, match.end - nodeStart);

      // Add text before highlight
      if (relativeStart > cursor) {
        fragments.push(document.createTextNode(text.slice(cursor, relativeStart)));
      }

      // Add highlight
      const highlight = document.createElement('span');
      Object.assign(highlight.style, DEFAULT_HIGHLIGHT_STYLE);
      highlight.textContent = text.slice(relativeStart, relativeEnd);
      fragments.push(highlight);

      cursor = relativeEnd;
    });

    // Add remaining text
    if (cursor < text.length) {
      fragments.push(document.createTextNode(text.slice(cursor)));
    }

    // Replace original node with fragments
    fragments.forEach((fragment) => parent.insertBefore(fragment, node));
    parent.removeChild(node);

    globalOffset = nodeEnd;
  };

  const processNode = (node: Node): void => {
    if (node.nodeType === Node.TEXT_NODE) {
      processTextNode(node as Text);
    } else {
      Array.from(node.childNodes).forEach(processNode);
    }
  };

  processNode(doc.body);
  return doc.body.innerHTML;
};

const convertRichTextToHtml = (content?: object, mentionItems: MentionItem[] = []): string | undefined => {
  if (!content) return undefined;

  try {
    const extensions = createRichTextExtensions({ mentionItems });
    return generateHTML(content, extensions);
  } catch {
    return undefined;
  }
};

export function HighlightableRichText({
  text,
  searchTerm,
  maxLength = 1000,
  richTextContent,
  ...textProps
}: HighlightableTextProps) {
  // Convert rich text to HTML
  const richHtml = useMemo(() => {
    if (!richTextContent) return undefined;
    const mentionItems: MentionItem[] = (richTextContent as JSONContent)?.mentions ?? [];
    return convertRichTextToHtml(richTextContent, mentionItems);
  }, [richTextContent]);

  const content = richHtml || text;

  // Handle text expansion/truncation
  const { shouldTruncate, toggleExpansion, truncatedContent, expanded } = useExpandableText({
    content,
    maxLength,
    searchTerm,
    isHtml: !!richHtml,
  });

  return (
    <>
      <Text
        {...textProps}
        dangerouslySetInnerHTML={{
          __html: highlightHtmlContent(truncatedContent, searchTerm),
        }}
      />

      {shouldTruncate && (
        <Anchor component="button" type="button" size="sm" onClick={toggleExpansion} style={{ marginTop: 4 }}>
          {expanded ? 'Show less' : 'Show more'}
        </Anchor>
      )}
    </>
  );
}
